<script lang="ts" setup>
  import type { ButtonProps, InputInst } from 'naive-ui'
  import type { MaybeRefOrGetter } from 'vue'

  import useVuelidate from '@vuelidate/core'

  import { useInlineEditableField, useValidationErrorText } from '@/shared/lib'
  import { InlineEditableTextTrigger } from '@/shared/ui/components'

  export interface InlineEditableListItem {
    id?: number
    name: string
  }

  const props = withDefaults(
    defineProps<{
      allowEmptyDiscardOnBlur?: boolean
      deleteAriaLabel?: string
      deleteConfirmLabel?: string
      deleteQuestion: string
      draftValidationRules: MaybeRefOrGetter<Record<string, unknown>>
      inputTestId: string
      maxLength?: number
      placeholder: string
      rowTestId: string
      showDeleteButton: boolean
      triggerTestId: string
    }>(),
    {
      allowEmptyDiscardOnBlur: false,
    },
  )

  const emit = defineEmits<{
    (e: 'delete'): void
    (e: 'discard'): void
  }>()

  const model = defineModel<InlineEditableListItem>({ required: true })

  const { t } = useI18n()
  const getValidationErrorText = useValidationErrorText()

  const deleteConfirmLabelText = computed(() => props.deleteConfirmLabel ?? t('base_delete'))
  const deleteAriaLabelText = computed(() => props.deleteAriaLabel ?? t('base_delete'))

  const inputId = useId()
  const errorId = useId()

  const confirmNegativeButtonProps: ButtonProps = {
    size: 'tiny',
    strong: true,
    round: true,
    themeOverrides: {
      border: '1px solid var(--color-border)',
    },
  }
  const confirmPositiveButtonProps: ButtonProps = {
    size: 'tiny',
    strong: true,
    round: true,
    secondary: true,
  }

  const validationRule = computed(() => ({
    draft: toValue(props.draftValidationRules),
  }))

  const draftRef = ref(model.value.name)
  const v$ = useVuelidate(validationRule, { draft: draftRef }, { $autoDirty: true })

  const { draft, handleCommit, handleKeydown, isEditing, startEditing } = useInlineEditableField({
    sourceValue: () => model.value.name,
    toDraft: (value) => value,
    fromDraft: (value) => value,
    isEqual: (left, right) => left === right,
    resetValidation: () => v$.value.$reset(),
    validate: async () => v$.value.$validate(),
    onCommit: (name) => (model.value = { ...model.value, name }),
  })

  watch(draft, (value) => {
    draftRef.value = value
  })

  const inputRef = ref<InputInst | null>(null)

  watch(
    isEditing,
    async (editing) => {
      if (!editing) {
        return
      }

      await nextTick()
      inputRef.value?.focus()
    },
    { flush: 'post' },
  )

  const isInvalid = computed(() => v$.value.$invalid)

  const draftErrorText = computed(() => getValidationErrorText(v$.value.draft.$errors[0]))

  // Пустой draft без изменений: либо discard строки, либо оставляем edit mode
  const isEmptyUnchanged = () => draft.value.trim() === '' && model.value.name.trim() === ''

  const handleEmptyUnchanged = () => {
    if (props.allowEmptyDiscardOnBlur) {
      emit('discard')
    }
  }

  const handleBlur = async () => {
    if (isEmptyUnchanged()) {
      handleEmptyUnchanged()
      return
    }

    await handleCommit()
  }

  const handleRowKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && isEmptyUnchanged()) {
      event.preventDefault()
      handleEmptyUnchanged()
      return
    }

    handleKeydown(event)
  }

  const validate = async () => {
    if (isEditing.value) {
      await handleCommit()
    }

    const isValid = await v$.value.$validate()

    if (!isValid && !isEditing.value) {
      startEditing(true)
      await nextTick()
    }

    return isValid
  }

  defineExpose({
    isInvalid,
    validate,
  })
</script>

<template>
  <div
    class="flex items-start gap-2 py-3"
    :data-testid="rowTestId"
  >
    <div class="flex min-h-10 min-w-0 flex-1 flex-col justify-center">
      <InlineEditableTextTrigger
        v-if="!isEditing"
        :trigger-aria-label="placeholder"
        :test-id="triggerTestId"
        @activate="startEditing"
      >
        <slot name="display">
          <span class="body-lg pl-3">
            {{ model.name }}
          </span>
        </slot>
      </InlineEditableTextTrigger>

      <template v-else>
        <NInput
          ref="inputRef"
          v-model:value="draft"
          :placeholder="placeholder"
          :maxlength="maxLength"
          :status="v$.draft.$error ? 'error' : undefined"
          :data-testid="inputTestId"
          :input-props="{
            id: inputId,
            'aria-required': 'true',
            'aria-invalid': v$.draft.$error,
            'aria-describedby': v$.draft.$error ? errorId : undefined,
          }"
          @blur="handleBlur"
          @keydown="handleRowKeydown"
        >
          <template
            v-if="maxLength !== undefined"
            #suffix
          >
            <span class="body-sm text-body-secondary">{{ draft.length }}/{{ maxLength }}</span>
          </template>
        </NInput>

        <p
          v-if="v$.draft.$error"
          :id="errorId"
          class="body-sm text-error! m-0 mt-0.5"
          role="alert"
          aria-live="polite"
        >
          {{ draftErrorText }}
        </p>
      </template>
    </div>

    <NPopconfirm
      v-if="showDeleteButton"
      :show-icon="false"
      :positive-text="deleteConfirmLabelText"
      :negative-text="$t('base_cancel')"
      :negative-button-props="confirmNegativeButtonProps"
      :positive-button-props="confirmPositiveButtonProps"
      @positive-click="emit('delete')"
    >
      <template #trigger>
        <NButton
          quaternary
          round
          class="mt-0.5 shrink-0"
          :aria-label="deleteAriaLabelText"
          :data-testid="`${rowTestId}-delete`"
        >
          <i
            class="fa-regular fa-trash text-body"
            aria-hidden="true"
          />
        </NButton>
      </template>
      <div class="w-full text-center">
        {{ deleteQuestion }}
      </div>
    </NPopconfirm>
  </div>
</template>
